import moment, { Moment } from 'moment-timezone';
import _ from 'lodash';
import StatusHelper from '@wix/wixrest-utils/dist/openrest-helpers/Status';
import {
  unionAvailabilityExceptions,
  unionWeeklyAvailabilities,
} from './unionAvailabilites';
import { isAvailableOn, intersectWeeklyAvailabilities } from 'availability';
import { DispatchInfo, IntegerInterval, Restaurant } from '../types/Restaurant';
import { DispatchType } from '../types/Dispatch';
import { getDelayMins } from './restaurantLogic';
import { Availability } from '../types/Availability';
import { simpleMemoize } from './simpleMemoize';
import { BusinessNotifications } from '../types/BusinessNotifications';

interface getAsapParamsArgs {
  deliveryInfos: DispatchInfo[];
  dispatchType: DispatchType;
  idealDeliveryArea?: DispatchInfo;
}

interface AsapParams {
  type: string;
  value?: number;
  min?: number;
  max?: number;
}

export type TimingOption = 'asap' | 'later';

export function getAvailableTimingOptions(
  restaurant: Restaurant,
  dispatchType: DispatchType,
  dispatchTime?: number,
  idealDeliveryArea?: DispatchInfo,
  businessNotification?: BusinessNotifications,
): TimingOption[] {
  const { timezone } = restaurant;
  const initialTime = moment(dispatchTime).tz(timezone);
  const numberOfDays = restaurant.orders.future.delayMins?.max / 1440;
  const finalAvailability = getFinalAvailability(
    restaurant,
    dispatchType,
    idealDeliveryArea,
  );
  const dateOptions = getDateOptions({
    availability: finalAvailability,
    timezone,
    numberOfDays,
    delayMins: restaurant.orders.future.delayMins,
  });
  const selectedDate = dispatchTime
    ? initialTime.startOf('day').valueOf()
    : dateOptions[0]?.timestamp;
  const earliestTimeOptions = dateOptions[0]?.timestamp
    ? getTimeOptions({
        availability: finalAvailability,
        day: selectedDate,
        timezone,
        delayMins: restaurant.orders.future.delayMins,
      })
    : [];
  const isOnlyAsap =
    restaurant.orders.future.disabled && !restaurant.orders.asap.disabled;
  const isOnlyFutureOrders =
    dispatchType === 'delivery'
      ? businessNotification &&
        businessNotification === BusinessNotifications.OnlyFutureOrders
      : !restaurant.orders.future.disabled &&
        earliestTimeOptions[0]?.timestamp &&
        moment(earliestTimeOptions[0].timestamp).tz(timezone) >
          moment()
            .tz(timezone)
            .add(restaurant.orders.future.delayMins.min + 15, 'minutes');

  const isAsapDisabled = Boolean(restaurant.orders.asap.disabled);

  let result: TimingOption[] = ['asap', 'later'];

  if (isOnlyAsap) {
    result = result.filter(x => x === 'asap');
  }

  if (isOnlyFutureOrders) {
    result = result.filter(x => x === 'later');
  }

  if (isAsapDisabled) {
    result = result.filter(x => x !== 'asap');
  }

  return result;
}

export function getAvailableDeliveryInfos(
  deliveryInfos: DispatchInfo[],
): DispatchInfo[] {
  return deliveryInfos.filter((deliveryInfo) => {
    const { status, reason } = StatusHelper.getStatus(
      deliveryInfo.availability,
      moment(),
    );
    const isClosed = status === 'unavailable' && reason === 'closed';
    return deliveryInfo.inactive !== false && !isClosed;
  });
}

export function getAsapParams({
  idealDeliveryArea,
  deliveryInfos,
  dispatchType,
}: getAsapParamsArgs): AsapParams {
  const availableDeliveryInfos = getAvailableDeliveryInfos(deliveryInfos);
  return dispatchType === 'delivery'
    ? getAsapParamsDelivery(availableDeliveryInfos, idealDeliveryArea)
    : getAsapParamsTakeout(availableDeliveryInfos);
}

export function getAsapParamsTakeout(
  deliveryInfos: DispatchInfo[],
): AsapParams {
  const takeout = _.find(deliveryInfos, (info) => info.type === 'takeout');
  let type, value;
  if (!takeout) {
    type = 'invalid';
  } else if (takeout.inactive) {
    type = 'unavailable';
  } else {
    type = takeout.delayMins !== 0 ? 'exact' : 'immediate';
    value = takeout.delayMins;
  }

  return { type, value };
}

export function getAsapParamsDelivery(
  deliveryInfos: DispatchInfo[],
  idealDeliveryArea?: DispatchInfo,
): AsapParams {
  const delayMins = idealDeliveryArea
    ? [idealDeliveryArea.delayMins || 0]
    : getDelayMins({ deliveryInfos, dispatchType: 'delivery' });

  let type;
  if (delayMins.length === 0) {
    return { type: 'unavailable' };
  } else if (delayMins.length === 1) {
    type = delayMins[0] === 0 ? 'immediate' : 'exact';
    return { type, value: delayMins[0] };
  } else {
    const min = _.min(delayMins);
    const max = _.max(delayMins);
    if (min === 0) {
      return { type: 'exact', value: max };
    } else {
      return { type: 'range', min, max };
    }
  }
}

const _isAvailableOn = simpleMemoize(
  (availability: Availability, time: Moment) =>
    isAvailableOn(availability, time),
);

export interface DateTimeOption {
  timestamp: number;
  displayableValue: string;
}

function isAvailableSomeTimeOnDay(
  availability: Availability,
  day: number,
  timezone: string,
  delayMins?: IntegerInterval,
  dishPrepareTime?: number,
) {
  const timeOptions = getTimeOptions({
    availability,
    day,
    timezone,
    delayMins,
    dishPrepareTime,
  });
  return (
    timeOptions.length > 0 &&
    getDaysDiff(timeOptions[0].timestamp, timezone, day) === 0
  );
}

export const getDateOptions = simpleMemoize(
  ({
    numberOfDays = 7,
    availability = {},
    timezone,
    delayMins,
    dishPrepareTime,
  }: {
    numberOfDays?: number;
    availability?: Availability;
    timezone: string;
    delayMins?: IntegerInterval;
    dishPrepareTime?: number;
  }) => {
    const result: DateTimeOption[] = [];

    const minimalWaitTime = Math.max(delayMins?.min || 0, dishPrepareTime || 0);

    const startDate = moment()
      .tz(timezone)
      .add(minimalWaitTime, 'minutes')
      .startOf('day');

    for (let i = 0; i < numberOfDays; i++) {
      const day = startDate.clone().add(i, 'days');

      if (
        delayMins &&
        day.diff(startDate, 'minutes') > delayMins.max - minimalWaitTime
      ) {
        break;
      }

      if (
        isAvailableSomeTimeOnDay(
          availability,
          day.valueOf(),
          timezone,
          delayMins,
          dishPrepareTime,
        )
      ) {
        result.push({
          timestamp: day.toDate().getTime(),
          displayableValue: day.format('MMMM Do'),
        });
      }
    }
    return result;
  },
);

export const getTimeOptions = simpleMemoize(
  ({
    day,
    timezone,
    locale = '',
    availability = {},
    delayMins,
    dishPrepareTime,
  }: {
    day: number;
    timezone: string;
    locale?: string;
    availability?: Availability;
    delayMins?: IntegerInterval;
    dishPrepareTime?: number;
  }) => {
    const result: DateTimeOption[] = [];
    const earliest = moment()
      .tz(timezone)
      .add(Math.max(delayMins?.min || 0, dishPrepareTime || 0), 'minutes');
    const isAtEarliestDay =
      getDaysDiff(day, timezone, earliest.valueOf()) === 0;
    const anchor = isAtEarliestDay
      ? getRoundedMinutesValue(earliest)
      : moment(day).tz(timezone).startOf('day');
    const endOfDay = anchor.clone().endOf('day');
    const endAnchor = delayMins
      ? moment.min(moment().add(delayMins.max, 'minutes'), endOfDay)
      : endOfDay;

    while (anchor.valueOf() < endAnchor.valueOf()) {
      if (_isAvailableOn(availability, anchor)) {
        result.push({
          timestamp: anchor.valueOf(),
          displayableValue: anchor.locale(locale).format('LT'),
        });
      }
      anchor.add(15, 'minutes');
    }

    return result;
  },
);

function getRoundedMinutesValue(m: Moment, interval: number = 15): Moment {
  if (interval <= 0 || interval > 60) {
    throw new Error(`Invalid interval ${interval}`);
  }

  const roundedMinutes = Math.ceil(m.minute() / interval) * interval;
  return m.clone().minute(roundedMinutes).seconds(0).milliseconds(0);
}

export function getDaysDiff(
  timestamp: number,
  timezone: string,
  comparedTo?: number,
) {
  return moment(timestamp)
    .tz(timezone)
    .startOf('day')
    .diff(moment(comparedTo).tz(timezone).startOf('day'), 'day');
}

function getAvailabilitiesByDispatchType(
  restaurant: Restaurant,
  dispatchType: DispatchType,
): Availability[] {
  const deliveryInfos = getAvailableDeliveryInfos(restaurant.deliveryInfos);
  return deliveryInfos
    .filter((di) => di.type === dispatchType)
    .map((di) => di.availability);
}

export function getFinalAvailability(
  restaurant: Restaurant,
  dispatchType: DispatchType,
  selectedDeliveryArea?: DispatchInfo,
  includeExceptions?: boolean,
): Availability {
  const availabilities =
    selectedDeliveryArea && dispatchType === 'delivery'
      ? [selectedDeliveryArea.availability]
      : getAvailabilitiesByDispatchType(restaurant, dispatchType);

  const { weekly }: Availability = intersectWeeklyAvailabilities(
    restaurant.openTimes,
    unionWeeklyAvailabilities(availabilities),
  );

  const exceptions = unionAvailabilityExceptions(availabilities);

  const filteredExceptions = [...exceptions.filter(
    (e) => e.available || includeExceptions,
  ), ...(restaurant.openTimes.exceptions || [])];

  const result: Availability = {
    weekly: (weekly || []).filter((x) => x.durationMins > 0),
  };

  if (filteredExceptions.length > 0) {
    result.exceptions = filteredExceptions;
  }

  return result;
}
